// GuidesIndex.js
// The /guides landing page. Lists published guides as cards. Sets its own
// page-level SEO (title + meta + CollectionPage JSON-LD). Only renders guides
// where published === true, so planned-but-unwritten entries in guidesData
// stay hidden until their component exists.

import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import GuidesLayout from './GuidesLayout';
import { getPublishedGuides, colors } from './guidesData';

const SITE = 'https://teetimeorganizer.net';

function useIndexSeo(guides) {
  useEffect(() => {
    document.title = 'Golf Group & League Guides | Tee Time Organizer';

    let desc = document.querySelector('meta[name="description"]');
    if (!desc) {
      desc = document.createElement('meta');
      desc.name = 'description';
      document.head.appendChild(desc);
    }
    desc.content =
      'Practical guides for golf group and league organizers: how to organize a weekly group, run a league, handle handicaps and pairings, and automate the busywork.';

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', `${SITE}/guides`);

    const id = 'guides-index-jsonld';
    let script = document.getElementById(id);
    if (!script) {
      script = document.createElement('script');
      script.type = 'application/ld+json';
      script.id = id;
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: 'Golf Group & League Guides',
      url: `${SITE}/guides`,
      hasPart: guides.map((g) => ({
        '@type': 'Article',
        headline: g.title,
        url: `${SITE}/guides/${g.slug}`,
      })),
    });
  }, [guides]);
}

export default function GuidesIndex() {
  const navigate = useNavigate();
  const guides = getPublishedGuides();
  useIndexSeo(guides);

  return (
    <GuidesLayout maxWidth={920}>
      <div style={{ marginBottom: 32 }}>
        <h1
          style={{
            fontFamily: "'DM Serif Display', Georgia, serif",
            fontSize: 38,
            fontWeight: 400,
            color: colors.gray900,
            margin: '0 0 10px',
            lineHeight: 1.15,
          }}
        >
          Guides for golf group organizers
        </h1>
        <p style={{ fontSize: 17, color: colors.gray600, lineHeight: 1.6, margin: 0, maxWidth: 620 }}>
          Practical, no-fluff playbooks for the person who runs the group — organizing,
          handicaps, pairings, and how to stop doing the parts a computer should handle.
        </p>
      </div>

      {guides.length === 0 ? (
        <p style={{ color: colors.gray500 }}>Guides are coming soon.</p>
      ) : (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: 20,
          }}
        >
          {guides.map((g) => (
            <article
              key={g.slug}
              onClick={() => navigate(`/guides/${g.slug}`)}
              style={{
                background: 'white',
                border: `1px solid ${colors.gray200}`,
                borderRadius: 14,
                padding: 22,
                cursor: 'pointer',
                transition: 'box-shadow 0.15s ease, transform 0.15s ease',
                display: 'flex',
                flexDirection: 'column',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.boxShadow = '0 8px 24px rgba(0,0,0,0.08)';
                e.currentTarget.style.transform = 'translateY(-2px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = 'none';
                e.currentTarget.style.transform = 'none';
              }}
            >
              <h2
                style={{
                  fontFamily: "'DM Serif Display', Georgia, serif",
                  fontSize: 21,
                  fontWeight: 400,
                  color: colors.gray900,
                  margin: '0 0 10px',
                  lineHeight: 1.25,
                }}
              >
                {g.title}
              </h2>
              <p
                style={{
                  fontSize: 14,
                  color: colors.gray600,
                  lineHeight: 1.6,
                  margin: '0 0 16px',
                  flexGrow: 1,
                }}
              >
                {g.excerpt}
              </p>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  fontSize: 13,
                }}
              >
                <span style={{ color: colors.green700, fontWeight: 700 }}>Read guide →</span>
                <span style={{ color: colors.gray400 }}>{g.readMinutes} min read</span>
              </div>
            </article>
          ))}
        </div>
      )}
    </GuidesLayout>
  );
}
